import { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { usePortalRoot } from '../../context/PortalContext';
import { useDropdownPosition } from '../../hooks/useDropdownPosition';
import './ui-transforms.css';
import './combobox.css';

/**
 * Combobox component - editable input with dropdown suggestions
 * Used for numeric range filters (price, year, mileage)
 *
 * On mobile: readOnly (no keyboard, dropdown-only selection)
 * On desktop: editable input with dropdown suggestions
 *
 * @param {string} variant - 'default' | 'joined-left' | 'joined-right'
 *   - 'default': standalone input with full border radius
 *   - 'joined-left': left part of joined pair (rounded left corners only)
 *   - 'joined-right': right part of joined pair (rounded right corners only)
 */
const Combobox = ({
  id,
  placeholder,
  value,
  onChange,
  options = [],
  formatOption,
  formatValue,
  prefix,
  className = '',
  inputClassName = '',
  variant = 'default',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);
  const dropdownRef = useRef(null);
  const portalRoot = usePortalRoot();

  // Detect mobile for readOnly behavior (no keyboard on mobile)
  const [isMobile, setIsMobile] = useState(window.innerWidth < 640);
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 639px)');
    const handler = (e) => setIsMobile(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  // Dynamic dropdown positioning via shared hook
  const handleCloseAll = useCallback(() => setIsOpen(false), []);
  const dropdownPosition = useDropdownPosition(isOpen, containerRef, dropdownRef, {
    defaultMaxHeight: 230,
    onCloseAll: handleCloseAll,
  });

  // Coordinate with other comboboxes: when one opens, others close.
  // Uses CustomEvent because Shadow DOM prevents document-level click detection.
  useEffect(() => {
    if (isOpen) {
      window.dispatchEvent(new CustomEvent('dropdown-open', { detail: { id } }));
    }
  }, [isOpen, id]);

  useEffect(() => {
    const handleOtherOpen = (e) => {
      if (e.detail.id !== id) {
        setIsOpen(false);
      }
    };
    window.addEventListener('dropdown-open', handleOtherOpen);
    return () => window.removeEventListener('dropdown-open', handleOtherOpen);
  }, [id]);

  // Close on click outside
  // Используем composedPath() для корректной работы в Shadow DOM
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!containerRef.current && !dropdownRef.current) return;

      // composedPath() показывает полный путь события включая Shadow DOM
      // event.target может быть retargeted на shadow host, но composedPath() работает корректно
      const path = event.composedPath ? event.composedPath() : [event.target];
      const isInsideContainer = containerRef.current && path.includes(containerRef.current);
      const isInsideDropdown = dropdownRef.current && path.includes(dropdownRef.current);

      if (!isInsideContainer && !isInsideDropdown) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, []);

  const handleBlur = (e) => {
    // On mobile readOnly mode, blur doesn't carry input value changes
    if (isMobile) return;

    setTimeout(() => {
      const val = e.target.value;
      if (val === '') {
        onChange(null);
      } else {
        const parsed = parseInt(val);
        if (!isNaN(parsed)) {
          onChange(parsed);
        }
      }
      // Close dropdown when input loses focus (e.g. tapping another filter)
      // Timeout ensures dropdown option click is processed before closing
      setIsOpen(false);
    }, 150);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      setIsOpen(false);
      e.target.blur();
    }
    if (e.key === 'Escape') {
      setIsOpen(false);
    }
  };

  const handleOptionSelect = (option) => {
    if (option === 0) {
      onChange(null);
    } else {
      onChange(option);
    }
    setIsOpen(false);
  };

  const displayFormatOption = formatOption || ((val) => val === 0 ? 'Any' : val.toString());
  const displayFormatValue = formatValue || ((val) => val?.toString() || '');

  // Border radius based on variant
  const radiusClass = {
    'default': 'combobox__input',
    'joined-left': 'combobox__input--joined-left',
    'joined-right': 'combobox__input--joined-right',
  }[variant];

  // Рамка и фокус описаны в combobox.css: joined-варианты сидят внутри общей
  // рамки обёртки, поэтому своей у них нет (#1323).

  // On mobile: hide prefix (€) when dropdown is closed and no value entered
  // so placeholder text ("Price from") fits fully at 16px font-size
  const hidePrefixOnMobile = prefix && !isOpen && value === null;

  return (
    <div ref={containerRef} className={`combobox ${inputClassName} ${className}`.trim()}>
      {prefix && (
        <span className={`combobox__prefix ui-center-y${hidePrefixOnMobile ? ' combobox__prefix--hidden-mobile' : ''}`}>
          {prefix}
        </span>
      )}
      <input
        id={id}
        type="text"
        inputMode="numeric"
        autoComplete="off"
        readOnly={isMobile}
        placeholder={placeholder}
        defaultValue={value ?? ''}
        key={`${id}-${value ?? 'empty'}`}
        onClick={() => setIsOpen(prev => !prev)}
        onTouchEnd={(e) => {
          // preventDefault stops the browser from converting touch to click
          // (avoids double-fire: touchend + click = double toggle)
          // NOTE: removed e.stopPropagation() — was blocking click-outside detection
          e.preventDefault();
          setIsOpen(prev => !prev);
        }}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        className={`${radiusClass} ui-touch-action${
          prefix
            ? hidePrefixOnMobile
              ? ' combobox__input--with-prefix combobox__input--prefix-hidden-mobile'
              : ' combobox__input--with-prefix'
            : ''
        }`}
      />
      {isOpen && options.length > 0 && createPortal(
        <div
          ref={dropdownRef}
          data-portal-dropdown="true"
          className="combobox__dropdown"
          style={{
            position: 'fixed',
            top: dropdownPosition.top,
            left: dropdownPosition.left,
            // Dropdown may need to be wider than its trigger — otherwise long
            // option labels like "€10,000" get truncated to "€10,0" in narrow
            // joined-range inputs. Floor at 8rem (128px) which fits up to
            // "€1,000,000" in the default font.
            minWidth: '8rem',
            width: dropdownPosition.width,
            maxHeight: dropdownPosition.maxHeight,
            zIndex: 9999,
          }}
        >
          {options.map((option) => (
            <button
              key={option}
              type="button"
              onMouseDown={(e) => {
                e.preventDefault();
                handleOptionSelect(option);
              }}
              onTouchEnd={(e) => {
                e.preventDefault();
                handleOptionSelect(option);
              }}
              className={`combobox__option${value === option ? ' combobox__option--selected' : ''}`}
            >
              {displayFormatOption(option)}
            </button>
          ))}
        </div>,
        portalRoot
      )}
    </div>
  );
};

export { Combobox };
export default Combobox;
